import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './home.css';
import { supabase } from '../../lib/supabaseClient';
import ScHeader from '../../components/ScHeader';
import {
  APP_BASENAME,
  buildSleepCareCheckoutRedirectUrl,
  buildSleepCareCustomData,
  fetchCohortCheckoutPaid,
  finalizeSleepCarePaymentSuccess,
  PORTONE_DUMMY_PHONE,
  PRICING_PROMO_CODE,
  randomPaymentId,
  requestSleepCarePortOnePayment,
  storeSleepCareCheckoutPending,
  clearSleepCareCheckoutPending,
} from '../../config/sleepCareCheckout';
import { COHORT_CARE_OPTION_IDS } from '../../config/sleepCareConstants';

import coach1 from '../../images/coach-1.png';
import coach2 from '../../images/coach-2.png';
import evidence1 from '../../images/evidence-1.png';
import evidence2 from '../../images/evidence-2.png';
import evidence3 from '../../images/evidence-3.png';
import evidence4 from '../../images/evidence-4.png';
import evidence5 from '../../images/evidence-5.png';
import featureImage1 from '../../images/image-1.png';
import featureImage2 from '../../images/image-2.png';
import featureImage3 from '../../images/image-3.png';
import heroBackgroundImage from '../../images/background.png';

const featureImages = [featureImage1, featureImage2, featureImage3];

/** KG이니시스 V2 일반결제: 구매자 휴대폰 필수. 별도 수집 없이 PG 요건만 충족하는 자리 번호 — `sleepCareCheckout.js` 와 동일 */

function userFromSupabaseAuthUser(authUser) {
  if (!authUser) return null;
  const meta = authUser.user_metadata || {};
  return {
    email: authUser.email,
    user_metadata: {
      full_name: meta.full_name ?? meta.name ?? authUser.email?.split('@')[0] ?? '',
      avatar_url: meta.avatar_url ?? meta.picture ?? null,
    },
  };
}

const features = [
  {
    title: '전문 코치의 그룹 수면케어',
    intro:
      '대한민국 No.1 국제공인 수면코치가 직접 관리하는 그룹 수면케어를 통해 당신의 수면 고민을 해결해 보세요.',
    bullets: [
      '국제공인 수면코치의 온라인 라이브 코칭 (1회)',
      '그룹챗을 통한 데일리 수면 관리',
      '그룹 구성원과의 수면 노하우 공유',
      '사전-사후 변화 리포트 제공',
    ],
  },
  {
    title: '근거기반 이완훈련',
    intro: '과학적인 유형별 맞춤 이완훈련을 통해 매일 밤 편안히 잠드세요.',
    bullets: [
      '체계적으로 설계된 주차별 이완훈련',
      '취침 전 10분, 언제 어디서나 해볼 수 있는 근거기반 실천법',
      '수면뿐만 아니라 마음건강까지 관리할 수 있는 효과적인 콘텐츠',
    ],
  },
  {
    title: '하루 60초, 셀프 피드백',
    intro:
      '간단한 셀프 피드백으로, 이완 경험을 ‘나’의 것으로 만들어 프로그램 효과를 극대화해보세요.',
    bullets: [
      '음성으로 간편하게 기록하는 나의 변화',
      'AI의 텍스트 자동 변환 기능',
      '캘린더에 쌓이는 나의 기록',
    ],
  },
];

/** 결제·DB 연동: care_slot_index 0~2 ↔ cohort_care_options.id (`COHORT_CARE_OPTION_IDS` 는 config) */

const sleepCareTypes = [
  {
    type: 'TYPE 01',
    cohortCareOptionId: COHORT_CARE_OPTION_IDS[0],
    title: '‘잠들기 전 복잡한 생각’ 케어',
    target: '끊이지 않는 생각과\n스트레스로 잠 못 이루는 분들',
    desc: '마음챙김 및 이완요법을 중심으로\n몸과 마음을 이완시켜 수면을 완성합니다.',
    imageUrl:
      'https://images.pexels.com/photos/935777/pexels-photo-935777.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    type: 'TYPE 02',
    cohortCareOptionId: COHORT_CARE_OPTION_IDS[1],
    title: '‘자는 동안 답답한 호흡’ 케어',
    target: '코골이가 심하거나\n자는 동안 숨이 답답한 분들',
    desc: '호흡법 및 구강근 강화 훈련을 중심으로\n불편한 호흡을 바로 잡고 수면을 완성합니다.',
    imageUrl:
      'https://images.pexels.com/photos/3771069/pexels-photo-3771069.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    type: 'TYPE 03',
    cohortCareOptionId: COHORT_CARE_OPTION_IDS[2],
    title: '‘한밤중 잦은 화장실’ 케어',
    target: '화장실 때문에 잠들기 어렵거나\n잠에서 자주 깨는 분들',
    desc: '골반저근운동 및 자율훈련을 중심으로\n자율신경 균형을 회복해 수면을 완성합니다.',
    imageUrl:
      'https://images.pexels.com/photos/271897/pexels-photo-271897.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
];

const reviews = [
  {
    content:
      '내가 수면문제를 겪고 있는 것의 원인이 무엇인지 알았고, 그것을 고치는 방법을 일반적인 방법이 아닌 맞춤형으로 제시해 주셔서 그 방법을 실천하였다. 정해진 시간에 충분한 릴렉스 타임을 가지고 잠자리에 드니 오래 걸리지 않아 잠에 들었다.',
    name: '서**',
    tag: '그룹 수면코칭',
    dateLabel: '2026, Mar',
    rating: 5,
  },
  {
    content:
      '덕분에 안정적인 수면 루틴을 잡게 되었습니다!\n참여하신 모든분들 다들 수고 많으셨습니다.\n같이 하면서 내적친밀감과\n수면 동기부여도 받았습니다 ㅎㅎ\n모두 다시 한 번 새해복 많이 받으시고,\n건강하세요!',
    name: null,
    tag: '그룹 수면코칭',
    dateLabel: '2025, Dec',
    rating: 5,
  },
  {
    content:
      '다른 분들과 같이 참여하니\n끝까지 잊지 않고 마무리한 것 같고,\n매일 피드백주셔서 포기하지 않았던 것 같다.',
    name: null,
    tag: '그룹 수면코칭',
    dateLabel: '2023, July',
    rating: 5,
  },
  {
    content:
      '인터넷에서 수면에 좋은 걸\n혼자 찾아서 이것저것 시도해보는 것이 아니라\n전문적으로 연구하신 분이 직접 코칭해주시니까\n보다 더 직접적인 도움이 되었습니다.',
    name: null,
    tag: '그룹 수면코칭',
    dateLabel: '2023, July',
    rating: 5,
  },
  {
    content:
      '수면에 대해 다시 생각해보고, 더 깊이 이해할 수 있었던 시간이었습니다. 그동안은 입면 시간에 대한 스트레스와 ‘빨리 자야 한다’는 강박 때문에 오히려 잠들기 어려운 경우가 많았는데, 수면을 나를 위한 쉼이라고 바라보게 되면서 잠에 대한 부담이 많이 줄어들었습니다.',
    name: '이**',
    tag: '그룹 수면코칭',
    dateLabel: '2026, Mar',
    rating: 5,
  },
];

const curriculumWeeks = [
  {
    week: 'Week 1. 잔여긴장 해소훈련',
    tags: ['#과날숨 호흡법', '#신체감각 앵커링', '#잔여긴장 이완암시'],
    bullets: [
      '수면이 망가진 원인을 이해하고 목표 설정',
      '평소 인지하지 못한 채 남아있는 미세한 긴장에 대한 알아차림',
      '깊은 호흡을 통한 미세한 잔여긴장의 해소 및 이완 경험',
    ],
  },
  {
    week: 'Week 2. 점진적 근이완훈련',
    tags: ['#하행식 PMR', '#신체감각 앵커링', '#햇볕 이완 심상'],
    bullets: [
      '미세한 긴장에서 몸 전체 근육의 긴장을 해소하는 것으로 확장',
      '신체 부위별로 긴장을 빠르게 이완하고, 회복하는 방법 터득',
      '신체 감각에 대한 알아차림과 ‘졸음 신호’에 대한 알아차림 연습',
    ],
  },
  {
    week: 'Week 3. 빠른리듬 횡경막훈련',
    tags: ['#횡격막 태핑', '#호흡감각 앵커링', '#FPDB 호흡법'],
    bullets: [
      '횡경막의 움직임을 통해 미주신경(부교감신경계) 활성',
      '생리적 과각성을 제어하는 비약물적 훈련 전략',
      '무너진 자율신경의 균형을 회복하고 깊은 이완 경험',
    ],
  },
  {
    week: 'Week 4. 무애착 심상훈련',
    tags: ['#마음챙김', '#명상인지테라피', '#탈관념 라벨 심상'],
    bullets: [
      '취침 전 10분, 이완훈련 루틴을 완성하고 일상적 영역에 적용',
      '신체적·생리적 각성에서 인지적·정서적 과각성을 완화하는 훈련으로 확장',
      '‘나’에 대한 경직되고 당위적인 사고에서 벗어나 자연스러운 수면의 경험 획득',
    ],
  },
];

function ReviewStars({ count = 5 }) {
  return (
    <span className="sc-review-stars" role="img" aria-label={`별점 ${count}점 만점`}>
      {Array.from({ length: count }, (_, i) => (
        <svg
          key={i}
          className="sc-review-star-icon"
          viewBox="0 0 24 24"
          width="12"
          height="12"
          aria-hidden
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z"
          />
        </svg>
      ))}
    </span>
  );
}

export default function Home() {
  const navigate = useNavigate();

  // 사전예약 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 로그인
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [pricingCode, setPricingCode] = useState('');
  const [hasPreOrderMatch, setHasPreOrderMatch] = useState(false);
  const [selectedCareIndex, setSelectedCareIndex] = useState(0);
  const [paymentBusy, setPaymentBusy] = useState(false);
  /** 현재 기수(COHORT_CARE_OPTION_IDS)에 이미 결제(지원) 완료 — 기수당 케어 1건 */
  const [cohortCheckoutPaid, setCohortCheckoutPaid] = useState(false);

  const refreshCohortCheckoutStatus = useCallback(async () => {
    const paid = await fetchCohortCheckoutPaid();
    setCohortCheckoutPaid(paid);
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem('mock_user');
    if (saved) setUser(JSON.parse(saved));

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const u = userFromSupabaseAuthUser(session.user);
        if (u) {
          sessionStorage.setItem('mock_user', JSON.stringify(u));
          setUser(u);
          setLoginModalOpen(false);
        }
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const u = userFromSupabaseAuthUser(session.user);
        if (u) {
          sessionStorage.setItem('mock_user', JSON.stringify(u));
          setUser(u);
          setLoginModalOpen(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    refreshCohortCheckoutStatus();
  }, [user, refreshCohortCheckoutStatus]);

  /** @param {'self' | 'group'} plan */
  const handleSleepCareCheckout = async (plan = 'group') => {
    if (!user) return;
    if (plan === 'group' && cohortCheckoutPaid) {
      alert('이미 해당 기수의 케어 유형에 지원이 완료되었습니다.');
      return;
    }
    setPaymentBusy(true);
    try {
      const isSelf = plan === 'self';
      const selfHelpCohortCareOptionId = 'e1c8d9d9-d13f-416f-8d64-378bb89a68c4';
      const groupCoachingCohortCareOptionId = '328aa0f9-63bc-4d9a-8b04-170eb1d80a82';
      const totalAmount = isSelf ? 19000 : 79000;
      const customerName = user.user_metadata?.full_name || user.email?.split('@')[0] || '고객';
      const customerEmail = user.email || '';
      const redirectUrl = buildSleepCareCheckoutRedirectUrl(
        customerName,
        customerEmail,
        PORTONE_DUMMY_PHONE
      );

      const {
        data: { session },
      } = await supabase.auth.getSession();

      const cohortCareOptionId = isSelf
        ? selfHelpCohortCareOptionId
        : groupCoachingCohortCareOptionId;
      const careTypeIndex = isSelf ? -1 : 0;
      const preorderForPayment = false;
      const customData = buildSleepCareCustomData({
        hasPreOrderMatch: preorderForPayment,
        careTypeIndex,
        cohortCareOptionId,
        totalAmount,
        customerName,
      });

      storeSleepCareCheckoutPending({
        totalAmount,
        careTypeIndex,
        cohortCareOptionId,
        preorderPromoApplied: preorderForPayment,
        rawCustomData: customData,
      });

      const payment = await requestSleepCarePortOnePayment({
        paymentId: randomPaymentId(),
        totalAmount,
        customerName,
        customerEmail,
        customerPhone: PORTONE_DUMMY_PHONE,
        customData,
        redirectUrl,
      });

      if (payment.code !== undefined) {
        if (payment.code === 'PORTONE_USER_CANCELLED' || payment.code === 'PG_PROVIDER_ERROR') {
          clearSleepCareCheckoutPending();
          return;
        }
        clearSleepCareCheckoutPending();
        alert(payment.message || '결제에 실패했습니다.');
        return;
      }

      if (payment.paymentId) {
        const result = await finalizeSleepCarePaymentSuccess({
          paymentId: payment.paymentId,
          customerName,
          customerEmail,
          customerPhone: PORTONE_DUMMY_PHONE,
          authUserId: session?.user?.id ?? null,
          totalAmount,
        });
        if (result.success) {
          clearSleepCareCheckoutPending();
          await refreshCohortCheckoutStatus();
          alert('결제가 완료되었습니다.');
          navigate('/payments');
        } else {
          clearSleepCareCheckoutPending();
          alert(result.message || '결제 확인에 실패했습니다.');
        }
      }
    } catch (e) {
      console.error(e);
      clearSleepCareCheckoutPending();
      alert('결제 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setPaymentBusy(false);
    }
  };

  const checkPricingCode = () => {
    const raw = pricingCode.trim();
    if (!raw) {
      setHasPreOrderMatch(false);
      return;
    }
    const matched = raw.toLowerCase() === PRICING_PROMO_CODE;
    setHasPreOrderMatch(matched);
    if (matched) {
      alert('사전예약 코드가 적용되었습니다.');
    }
  };

  const handleGoogleLogin = async () => {
    const redirectTo = `${window.location.origin}${APP_BASENAME}/`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) {
      console.error(error);
      alert('Google 로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    sessionStorage.removeItem('mock_user');
    setUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (cohortCheckoutPaid) return;

    setLoading(true);
    setError('');

    const { error: insertError } = await supabase
      .from('pre_orders')
      .insert([{ email, phone, target_group: selectedGroup }]);

    setLoading(false);

    if (insertError) {
      setError('신청 중 오류가 발생했습니다. 다시 시도해 주세요.');
      return;
    }

    setSubmitted(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSubmitted(false);
    setEmail('');
    setPhone('');
    setSelectedGroup('');
  };

  return (
    <div className="sc-root">
      <ScHeader
        user={user}
        onLoginClick={() => setLoginModalOpen(true)}
        onUserUpdate={(updated) => {
          sessionStorage.setItem('mock_user', JSON.stringify(updated));
          setUser(updated);
        }}
        onLogout={handleLogout}
      />

      {/* ── HERO ── */}
      <section className="sc-hero">
        <div
          className="sc-hero-bg"
          style={{
            backgroundImage: `url(${heroBackgroundImage})`,
          }}
        />
        <div className="sc-hero-content">
          <h1 className="sc-hero-title">
            <span className="sc-hero-title-lead">
              잠을 자는 것도 <span className="sc-hero-highlight">기술</span>입니다.
              <br />
              배우면 바뀝니다.
              <br />
            </span>
            <span className="sc-hero-highlight">솜니아 슬립케어</span>
          </h1>
          <button
            className="sc-cta-btn sc-hero-btn"
            type="button"
            disabled={!!user && cohortCheckoutPaid}
            onClick={() => {
              if (!user) {
                setLoginModalOpen(true);
                return;
              }
              if (cohortCheckoutPaid) return;
              document
                .getElementById('sc-pricing')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            {user ? (cohortCheckoutPaid ? '지원 완료' : '프로그램 신청하기') : '프로그램 신청하기'}
          </button>
        </div>
        <div className="sc-hero-orb sc-hero-orb1" />
        <div className="sc-hero-orb sc-hero-orb2" />
      </section>

      {/* ── Problems ── */}
      <section className="sc-section sc-problems-section" aria-labelledby="sc-problems-heading">
        <div className="sc-container">
          <p className="sc-section-label">Problems</p>
          <p className="sc-problems-lead sc-section-title--space-wrap">
            운동, 영어, 업무…
            <br />
            못하면 다 배우면서,
          </p>
          <h2 id="sc-problems-heading" className="sc-problems-heading sc-section-title--space-wrap">
            왜 잠은 혼자 해결하고 있나요?
          </h2>
          <div className="sc-problems-image-wrap">
            <img
              className="sc-problems-image"
              src="https://images.pexels.com/photos/935777/pexels-photo-935777.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=2000"
              alt="편안하게 잠들어 있는 사람"
              loading="lazy"
            />
          </div>
          <p className="sc-problems-body sc-section-title--space-wrap">
            이제, 국제공인 수면코치의 4주 그룹 수면관리 프로그램{' '}
            <span className="sc-hero-highlight">솜니아 슬립케어</span>에서
            <br />
            ‘잘 자는 법’ 제대로 배워보세요.
          </p>
          <div className="sc-problems-asides">
            <aside className="sc-problems-aside">
              <span className="sc-problems-aside-mark" aria-hidden>
                <svg
                  className="sc-problems-check"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  aria-hidden
                  focusable="false"
                >
                  <path
                    d="M5.5 12.5 10 17 18.5 6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.35"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <p className="sc-problems-aside-text sc-section-title--space-wrap">
                “좋다는 건 다 해봤는데, 효과가 없어요…”
              </p>
            </aside>
            <aside className="sc-problems-aside">
              <span className="sc-problems-aside-mark" aria-hidden>
                <svg
                  className="sc-problems-check"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  aria-hidden
                  focusable="false"
                >
                  <path
                    d="M5.5 12.5 10 17 18.5 6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.35"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <p className="sc-problems-aside-text sc-section-title--space-wrap">
                “잠이 안 오는데 어떡해요. 그냥 누워만 있어요.”
              </p>
            </aside>
            <aside className="sc-problems-aside">
              <span className="sc-problems-aside-mark" aria-hidden>
                <svg
                  className="sc-problems-check"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  aria-hidden
                  focusable="false"
                >
                  <path
                    d="M5.5 12.5 10 17 18.5 6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.35"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <p className="sc-problems-aside-text sc-section-title--space-wrap">
                “병원에 가기는 싫은데, 잠은 잘 자고 싶어요.”
              </p>
            </aside>
          </div>
          <p className="sc-problems-closing sc-section-title--space-wrap">
            <strong>‘잠’은 의지로 드는 게 아니라,</strong>{' '}
            <strong>
              <span className="sc-hero-highlight">구조(system)</span>로 바꾸는 겁니다.
            </strong>
          </p>
        </div>
      </section>

      {/* ── Coach ── */}
      <section className="sc-section sc-credentials-section">
        <div className="sc-container">
          <p className="sc-section-label">Solutions</p>
          <h2 className="sc-section-title sc-section-title--space-wrap">
            솜니아 슬립케어에서
            <br />
            전문 수면코치와 함께 <span className="sc-hero-highlight">숙면의 구조</span>를
            설계해보세요.
          </h2>

          <div className="sc-coach-profile">
            <div className="sc-coach-images">
              <div className="sc-coach-img-box">
                <img src={coach1} alt="Brainer Jay 1" className="sc-coach-img" />
              </div>
              <div className="sc-coach-img-box">
                <img src={coach2} alt="Brainer Jay 2" className="sc-coach-img" />
              </div>
            </div>

            <div className="sc-coach-info">
              <div className="sc-coach-name">
                <span className="sc-name-kr">브레이너 제이</span>
                <span className="sc-name-en">Brainer Jay</span>
              </div>
              <ul className="sc-coach-list">
                <li>
                  <a
                    href="https://www.youtube.com/@DeepSleepGuide"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <strong>
                      80만 유튜브(Youtube)
                      <br />
                      ‘브레이너 제이의 숙면여행’ 운영자
                    </strong>
                  </a>
                </li>
                <li>
                  <strong>미국 공인 임상수면건강 전문가</strong>
                  <br />
                  (CCSH, 수면의학 상급자격)
                </li>
                <li>
                  <strong>영국 마음챙김지도자연합 인증 지도자</strong>
                  <br />
                  (MNT, 한국인 최초)
                </li>
                <li>
                  <strong>미국 국제인증 통합적 성인 수면코치</strong>
                  <br />
                  (IASC, 한국인 최초)
                </li>
                <li>
                  <strong>
                    영국 옥스퍼드대학교
                    <br />
                    임상신경과학과 수면의학 전공
                  </strong>
                </li>
                <li>
                  <strong>5년 연속 방송 출연</strong>(SBS, TV조선 등),
                  <br />
                  <strong>7년간 강연 다수</strong>(20여 대기업/병원 등)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="sc-section sc-features-section">
        <div className="sc-container">
          <p className="sc-section-label">Features</p>
          <h2 className="sc-section-title sc-section-title--space-wrap">
            <span className="sc-hero-highlight">취침 전 10분,</span>
            <br />
            배우고, 기록하고, 공유하면 달라집니다.
          </h2>
          <div className="sc-features-grid">
            {features.map((f, i) => (
              <div className="sc-feature-card" key={i}>
                <div className="sc-feature-icon">
                  <img
                    src={featureImages[i]}
                    alt={f.title}
                    className="sc-feature-icon-img"
                    loading="lazy"
                  />
                </div>
                <h3 className="sc-feature-title">{f.title}</h3>
                <p className="sc-feature-intro">
                  {f.intro.split('\n').map((line, idx) => (
                    <React.Fragment key={idx}>
                      {idx > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </p>
                <ul className="sc-feature-bullets">
                  {f.bullets.map((line, k) => (
                    <li key={k} className="sc-feature-bullet">
                      <span className="sc-feature-bullet-mark" aria-hidden>
                        <svg
                          className="sc-feature-check"
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                          aria-hidden
                          focusable="false"
                        >
                          <path
                            d="M5.5 12.5 10 17 18.5 6.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.35"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="sc-security-box">
            <div className="sc-security-header">
              <h3 className="sc-security-title">내 목소리가 안전하게 지켜질까?</h3>
              <p className="sc-security-sub">여러분의 개인정보를 안전하게 보호합니다.</p>
            </div>
            <ul className="sc-security-list">
              <li>모든 음성 데이터는 암호화되어 전송·저장됩니다.</li>
              <li>선택 동의 없이는 음성 원본이 저장되지 않습니다.</li>
              <li>음성이 외부에 공유되거나 유출되지 않습니다.</li>
              <li>제3자에게 음성 원본이 제공되지 않습니다.</li>
              <li>동의 철회 및 데이터 삭제를 요청할 수 있습니다.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── CURRICULUM ── */}
      <section className="sc-section sc-curriculum-section">
        <div className="sc-container">
          <p className="sc-section-label">Curriculum</p>
          <h2 className="sc-section-title">총 4주 동안, 매일 10분씩만 투자하세요.</h2>
          <div className="sc-curriculum-grid">
            {curriculumWeeks.map((item) => (
              <aside className="sc-curriculum-card" key={item.week}>
                <h3 className="sc-curriculum-week">{item.week}</h3>
                <div className="sc-curriculum-tags" aria-label="훈련 키워드">
                  {item.tags.map((tag) => (
                    <span className="sc-curriculum-tag-badge" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
                <ul className="sc-curriculum-list">
                  {item.bullets.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </aside>
            ))}
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section className="sc-section sc-reviews-section">
        <div className="sc-container">
          <p className="sc-section-label">Effectiveness</p>
          <h2 className="sc-section-title">
            그룹 수면코칭 참가자들의
            <br />
            생생한 체험 후기
          </h2>
          <div className="sc-reviews-grid">
            {reviews.map((r, i) => (
              <div className="sc-review-card" key={i}>
                <p className="sc-review-content">
                  {r.content.split('\n').map((line, idx, arr) => (
                    <React.Fragment key={idx}>
                      {line}
                      {idx < arr.length - 1 ? <br /> : null}
                    </React.Fragment>
                  ))}
                </p>
                <div className="sc-review-author">
                  <div className="sc-review-author-row">
                    <span>{r.name ?? '익명'}</span>
                    <ReviewStars count={r.rating} />
                  </div>
                  <span className="sc-review-date">{r.dateLabel}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EVIDENCE ── */}
      <section className="sc-section sc-evidence-section">
        <div className="sc-container">
          <p className="sc-section-label">Evidence</p>
          <h2 className="sc-section-title">
            대학병원들과 함께 증명해온
            <br />
            가장 과학적인 수면 솔루션
          </h2>
          <p className="sc-section-sub">
            최근 4년간, 유수 대학, 병원들과의 임상연구를 통해
            <br />
            솔루션의 효과성을 검증해왔습니다.
            <br />
            <br />
            솜니아 슬립케어는 이렇게 탄생한 과학적 수면/이완
            <br />
            콘텐츠와 코칭 프로그램을 담아 완성되었습니다.
          </p>

          <div className="sc-evidence-grid">
            {/* Evidence 3 + 4 */}
            <div className="sc-evidence-item sc-evidence-full sc-evidence-center-all">
              <div className="sc-evidence-img-group">
                <div className="sc-evidence-img-box">
                  <img src={evidence3} alt="Evidence 3" />
                </div>
                <div className="sc-evidence-img-box">
                  <img src={evidence4} alt="Evidence 4" />
                </div>
              </div>
              <p className="sc-evidence-caption sc-evidence-caption-center">
                ▲ 수면/이완 콘텐츠 사용 효과: 분당차병원 협력 무작위 대조군 실험(RCT) 연구(2025
                대한수면학회 보고)
              </p>
            </div>

            {/* Evidence 1 */}
            <div className="sc-evidence-item sc-evidence-center-all">
              <div className="sc-evidence-img-box">
                <img src={evidence1} alt="Evidence 1" />
              </div>
              <p className="sc-evidence-caption">▲ SCI(E)급 논문을 포함한 총 4편 논문 게재</p>
            </div>

            {/* Evidence 2 */}
            <div className="sc-evidence-item sc-evidence-center-all">
              <div className="sc-evidence-img-box">
                <img src={evidence2} alt="Evidence 2" />
              </div>
              <p className="sc-evidence-caption">▲ 국내외 수면학회 연구 보고 6건 및 학술 수상</p>
            </div>

            {/* Evidence 5 (Full width) */}
            <div className="sc-evidence-item sc-evidence-full sc-evidence-center-all">
              <div className="sc-evidence-img-box">
                <img src={evidence5} alt="Evidence 5" />
              </div>
              <p className="sc-evidence-caption">
                ▲ 여러 대기업/대학교/병원 등에 제공되어온 그룹 수면코칭 프로그램 (대면 방식)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="sc-pricing" className="sc-section sc-pricing-section">
        <div className="sc-container">
          <div className="sc-pricing-heading">
            <p className="sc-section-label">Price</p>
            <h2 className="sc-section-title sc-section-title--space-wrap">
              이미 할만큼 해봤잖아요.
              <br />더 이상 시간, 돈 낭비하지 마세요!
              <br />
              <br />
              가장 빠른 변화를 원한다면, 지금 시작하세요.
            </h2>
          </div>
          <div className="sc-pricing-card sc-pricing-card--plans">
            <div className="sc-plan-compare-scroll">
              <table className="sc-plan-compare-table">
                <thead>
                  <tr>
                    <th className="sc-plan-compare-corner" scope="col" />
                    <th scope="col">셀프헬프</th>
                    <th scope="col">그룹 코칭</th>
                    <th scope="col">퍼스널 코칭</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">이용 기간</th>
                    <td>4주</td>
                    <td>4주</td>
                    <td>4주</td>
                  </tr>
                  <tr>
                    <th scope="row">가격</th>
                    <td className="sc-plan-compare-cell-tight">
                      <span className="sc-plan-compare-muted">
                        정상가 <del>49,000</del>원
                      </span>{' '}
                      <span className="sc-plan-compare-discount-pct">(60%)</span>
                      <br />
                      <span className="sc-plan-compare-consumer-price">→ 특별 할인가 19,000원</span>
                    </td>
                    <td className="sc-plan-compare-cell-tight">
                      <span className="sc-plan-compare-muted">
                        정상가 <del>198,000</del>원
                      </span>{' '}
                      <span className="sc-plan-compare-discount-pct">(60%)</span>
                      <br />
                      <span className="sc-plan-compare-consumer-price">→ 특별 할인가 79,000원</span>
                    </td>
                    <td className="sc-plan-compare-cell-tight">
                      <span className="sc-plan-compare-consumer-price">견적 문의</span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">수면/이완요법</th>
                    <td>혼자 하는 4주 코스형</td>
                    <td>그룹 맞춤 4주 코스형</td>
                    <td>개인 맞춤 4주 코스형</td>
                  </tr>
                  <tr>
                    <th scope="row">사전-사후 설문/리포트</th>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">음성기반 다이어리</th>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">음성기반 셀프피드백</th>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">그룹 맞춤 수면 코칭</th>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">그룹챗 참여</th>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">일대일 맞춤 수면 코칭</th>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-dash">–</span>
                    </td>
                    <td>
                      <span className="sc-plan-compare-check" aria-hidden="true">
                        O
                      </span>
                    </td>
                  </tr>
                  <tr className="sc-plan-compare-actions-row">
                    <th scope="row">신청</th>
                    <td className="sc-plan-compare-action-cell">
                      <button
                        type="button"
                        className="sc-cta-btn sc-pricing-btn sc-plan-compare-action-btn"
                        aria-label="셀프헬프 결제하기"
                        // disabled={paymentBusy || (!!user && cohortCheckoutPaid)}
                        disabled={true}
                        onClick={() => {
                          if (paymentBusy) return;
                          if (!user) {
                            setLoginModalOpen(true);
                            return;
                          }
                          handleSleepCareCheckout('self');
                        }}
                      >
                        {/* {paymentBusy
                          ? '결제 처리 중...'
                          : user
                            ? cohortCheckoutPaid
                              ? '지원 완료'
                              : '결제하기'
                            : '로그인 후 결제하기'} */}
                        지원 마감
                      </button>
                    </td>
                    <td className="sc-plan-compare-action-cell">
                      <button
                        type="button"
                        className="sc-cta-btn sc-pricing-btn sc-plan-compare-action-btn"
                        aria-label="그룹 코칭 결제하기"
                        // disabled={paymentBusy || (!!user && cohortCheckoutPaid)}
                        disabled={true}
                        onClick={() => {
                          if (paymentBusy) return;
                          if (!user) {
                            setLoginModalOpen(true);
                            return;
                          }
                          handleSleepCareCheckout('group');
                        }}
                      >
                        {/* {paymentBusy
                          ? '결제 처리 중...'
                          : user
                            ? cohortCheckoutPaid
                              ? '지원 완료'
                              : '결제하기'
                            : '로그인 후 결제하기'} */}
                        지원 마감
                      </button>
                    </td>
                    <td className="sc-plan-compare-action-cell">
                      <a
                        className="sc-cta-btn sc-pricing-btn sc-plan-compare-mail-btn"
                        href="mailto:info@s-omni.com"
                        aria-label="퍼스널 코칭 견적 문의 메일 보내기"
                      >
                        견적 문의
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROGRAM PLAN ── */}
      <section className="sc-section sc-plan-section">
        <div className="sc-container">
          <p className="sc-section-label">Program Plan</p>
          <h2 className="sc-section-title">솜니아 슬립케어 참여 방법</h2>
          <div className="sc-plan-sub-section">
            <div className="sc-plan-steps-grid">
              {[
                {
                  num: '01',
                  title: '슬립케어 프로그램 신청',
                  desc: '- 슬립케어 프로그램 “신청” 버튼을 눌러 결제 및 신청을 완료하세요.',
                },
                {
                  num: '02',
                  title: '안내 메일 수신',
                  desc: '- 프로그램 시작 3~4일 전, 안내 메일이 발송돼요.\n- 오리엔테이션 일정과 프로그램 진행 일정을 꼭 확인하세요.\n- 오리엔테이션 전까지 ‘사전 설문’을 반드시 작성해주세요.',
                },
                {
                  num: '03',
                  title: '오리엔테이션 참가',
                  desc: '- 프로그램 시작 1일 전, 온라인(Live)로 진행되는 오리엔테이션에 참여해주세요.\n- 오리엔테이션은 마이크/카메라를 끄고 참여하실 수 있습니다.',
                  note: '※ 오리엔테이션에 불참할 경우, 프로그램 참여가 제한되며 참가비는 환불되지 않습니다.',
                },
                {
                  num: '04',
                  title: '슬립케어 프로그램 시작',
                  desc: '그룹별로 총 4주간 이완 훈련과 셀프 피드백을 진행해요.',
                },
                {
                  num: '05',
                  title: '슬립케어 프로그램 종료',
                  desc: '- 슬립케어 종료일에 ‘사후 설문’을 작성하고, 참여 전후 수면 변화 리포트를 확인해보세요.\n- 슬립케어 종료일로부터 7영업일 이내에 프로그램 수료증 및 리워드가 발송돼요.',
                },
              ].map((step, idx) => (
                <div className="sc-plan-step-item" key={idx}>
                  <span className="sc-step-badge">{step.num}</span>
                  <div className="sc-step-content">
                    <strong>{step.title}</strong>
                    <p>
                      {step.desc.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </p>
                    {step.note && (
                      <p className="sc-step-warning">
                        {step.note.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sc-plan-sub-section">
            <h3 className="sc-plan-sub-title">프로그램 리워드</h3>
            <div className="sc-plan-rewards-grid">
              <div className="sc-reward-card">
                <div className="sc-reward-icon">📊</div>
                <span className="sc-reward-label">Reward 1</span>
                <h4 className="sc-reward-name">변화 리포트</h4>
                <p className="sc-reward-desc">
                  14일 이상 미션 인증을 성공한 분들께는
                  <br />
                  프로그램 사전-사후 변화를 한 눈에 확인할 수 있는
                  <br />
                  4주 변화 리포트가 제공됩니다.
                </p>
              </div>
              <div className="sc-reward-card">
                <div className="sc-reward-icon">📜</div>
                <span className="sc-reward-label">Reward 2</span>
                <h4 className="sc-reward-name">참가 수료증</h4>
                <p className="sc-reward-desc">
                  21일 이상 미션 인증을 성공한 분들께는
                  <br />
                  솜니아 슬립케어 1기 참가 수료증을 제공합니다.
                  <br />
                  다음 기수 사전예약 시,
                  <br />
                  <strong>최대 70% 할인 혜택</strong>을 받을 수 있어요.
                </p>
              </div>
              <div className="sc-reward-card">
                <div className="sc-reward-icon">🎁</div>
                <span className="sc-reward-label">Reward 3</span>
                <h4 className="sc-reward-name">수면 보조 상품</h4>
                <p className="sc-reward-desc">
                  28일간 미션 인증을 100% 성공한 분들께는
                  <br />
                  추가적인 수면 보조 상품이 증정됩니다.
                  <br />
                  <small>※ 구체적인 상품 내용은 추후 안내 예정</small>
                </p>
              </div>
            </div>
          </div>

          <div className="sc-plan-details">
            <div className="sc-plan-info-card">
              <ul className="sc-plan-info-list">
                <li>
                  <span className="sc-plan-label">신청 기간</span>
                  <span className="sc-plan-value">2026년 04월 20일(월) ~ 04월 29일(수)</span>
                </li>
                <li>
                  <span className="sc-plan-label">오리엔테이션 DAY</span>
                  <div className="sc-plan-value-wrap">
                    <span className="sc-plan-value">2026년 05월 03일(일), 오후 9시</span>
                    <p className="sc-plan-note">
                      ※ 오리엔테이션은 줌 웨비나(Live)를 통해 비대면으로 약 40~50분분간 진행될
                      예정이에요.
                    </p>
                    <p className="sc-plan-note highlight">
                      ※ 오리엔테이션 불참 시, 프로그램 참여가 제한될 수 있으니 이 점 유의바랍니다.
                    </p>
                  </div>
                </li>
                <li>
                  <span className="sc-plan-label">프로그램 운영기간</span>
                  <div className="sc-plan-value-wrap">
                    <span className="sc-plan-value">2026년 05월 04일(월) ~ 05월 31일(일)</span>
                    <p className="sc-plan-note">
                      ※ 본 프로그램은 4주 동안 100% 온라인으로 진행되며, 익명(닉네임)으로 참여가
                      가능해요.
                    </p>
                    <p className="sc-plan-note highlight">
                      ※ 오리엔테이션 DAY부터는 그룹(유형) 변경 및 환불 등은 불가합니다.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="sc-plan-disclaimer">
              <p>
                <strong>DISCLAIMER:</strong>
                <br />본 프로그램은 국제공인 임상수면코치 ‘브레이너 제이(CCSH, IASC)’가 직접 교육 및
                관리하는 국내 최초 유형별 그룹 수면케어 프로그램으로,
                <br />
                다양한 건강 정보와 과학적인 훈련법이 함께 다뤄질 예정입니다. 하지만 의학적 진단이나
                처방, 치료 등을 대체하지 않습니다.
                <br />
                만성 및 중증 질환 또는 기타 질병을 가지신 분들은 반드시 담당 주치의를 통해 본
                프로그램 참여에 대한 조언을 받으시길 바랍니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="sc-footer">
        <div className="sc-footer-inner">
          <div className="sc-footer-brand">Somnia Sleep Care</div>
          <div className="sc-footer-info">
            <p>(주)에스옴니 | 대표: 유재성</p>
            <p>info@s-omni.com</p>
            <p>사업자등록번호: 231-86-02585</p>
            <p>서울특별시 중구 서애로5길 12-20, 4층</p>
            <p>전자통신판매업 신고번호: 제2023-서울중구-1199호</p>
            <p>
              <a
                href="https://s-omni.notion.site/SOMNIA-865ae2a018c349fd9936d2c4f62cd41d"
                target="_blank"
                rel="noopener noreferrer"
              >
                서비스 이용약관
              </a>{' '}
              |{' '}
              <a
                href="https://s-omni.notion.site/SOMNIA-09e46ce3f89a4065924bcd54bd3b2543"
                target="_blank"
                rel="noopener noreferrer"
              >
                개인정보처리방침
              </a>
            </p>
          </div>
        </div>
        <div className="sc-footer-copy">© 2026 S-OMNI, Inc. All rights reserved.</div>
      </footer>

      {/* ── MODAL ── */}
      {modalOpen && (
        <div className="sc-modal-overlay" onClick={closeModal}>
          <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="sc-modal-close" onClick={closeModal}>
              ✕
            </button>
            {submitted ? (
              <div className="sc-modal-success">
                <div className="sc-modal-success-icon">🌙</div>
                <h3>신청이 완료되었습니다!</h3>
                <p>
                  런칭 시 <strong>{email}</strong>으로
                  <br />
                  가장 먼저 안내드리겠습니다.
                </p>
                <button className="sc-cta-btn" onClick={closeModal}>
                  확인
                </button>
              </div>
            ) : (
              <>
                <div className="sc-modal-header">
                  <span className="sc-modal-badge">사전예약 신청</span>
                  <h3>가장 먼저 알림을 받으세요</h3>
                  <p>
                    아래 정보를 남겨주시면 솜니아 슬립케어 런칭 시<br />
                    가장 먼저 소식을 전해드립니다.
                  </p>
                </div>
                <form className="sc-modal-form" onSubmit={handleSubmit}>
                  <div className="sc-modal-field">
                    <label>이메일</label>
                    <input
                      type="email"
                      placeholder="이메일 주소를 입력해 주세요"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="sc-modal-field">
                    <label>연락처</label>
                    <input
                      type="tel"
                      placeholder="010-0000-0000"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>
                  <div className="sc-modal-field">
                    <label>참여 희망 그룹</label>
                    <select
                      value={selectedGroup}
                      onChange={(e) => setSelectedGroup(e.target.value)}
                      required
                      className="sc-modal-select"
                      disabled={cohortCheckoutPaid}
                    >
                      <option value="" disabled>
                        그룹을 선택해 주세요
                      </option>
                      <option value={sleepCareTypes[0].cohortCareOptionId}>
                        '잠들기 전 복잡한 생각' 케어
                      </option>
                      <option value={sleepCareTypes[1].cohortCareOptionId}>
                        '자는 동안 답답한 호흡' 케어
                      </option>
                      <option value={sleepCareTypes[2].cohortCareOptionId}>
                        '한밤중 잦은 화장실' 케어
                      </option>
                    </select>
                  </div>
                  {error && <p className="sc-modal-error">{error}</p>}
                  <button
                    type="submit"
                    className="sc-cta-btn sc-modal-submit"
                    disabled={loading || cohortCheckoutPaid}
                  >
                    {loading
                      ? '신청 중...'
                      : cohortCheckoutPaid
                        ? '지원 완료'
                        : '무료 사전예약 신청 →'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── LOGIN MODAL ── */}
      {loginModalOpen && (
        <div className="sc-modal-overlay" onClick={() => setLoginModalOpen(false)}>
          <div className="sc-modal sc-login-modal" onClick={(e) => e.stopPropagation()}>
            <button className="sc-modal-close" onClick={() => setLoginModalOpen(false)}>
              ✕
            </button>
            <div className="sc-modal-header">
              <span className="sc-modal-badge">로그인</span>
              <h3>
                솜니아 슬립케어에
                <br />
                오신 것을 환영합니다
              </h3>
              <p>소셜 계정으로 간편하게 로그인하세요.</p>
            </div>
            <div className="sc-login-btns">
              <button
                className="sc-login-social-btn sc-login-google"
                onClick={() => handleGoogleLogin()}
              >
                <svg
                  className="sc-login-social-icon"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Google로 로그인
              </button>
              {/* <button
                className="sc-login-social-btn sc-login-kakao"
                onClick={() => handleMockLogin('kakao')}
              >
                <svg
                  className="sc-login-social-icon"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 3C6.48 3 2 6.48 2 10.8c0 2.7 1.6 5.08 4.03 6.53L5 21l4.27-2.27C10.14 18.9 11.05 19 12 19c5.52 0 10-3.48 10-8.2C22 6.48 17.52 3 12 3z"
                    fill="#3A1D1D"
                  />
                </svg>
                카카오로 로그인
              </button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
